import type { ProviderUsageAccountView } from "~/usage/useProviderUsage";

/** Missing or expired observations cannot establish an account's current capacity. */
export function recoveryAccountCapacity(
  snapshot: ProviderUsageAccountView["snapshot"],
): "available" | "exhausted" | "unknown" {
  if (!snapshot || snapshot.stale) return "unknown";
  const windows = [snapshot.fiveHour, snapshot.sevenDay].filter((window) => window !== null);
  const current = windows.filter(
    (window) =>
      Number.isFinite(Date.parse(window.resetsAt)) && Date.parse(window.resetsAt) > Date.now(),
  );
  if (current.some((window) => window.status === "rejected" || window.usedPercent >= 100))
    return "exhausted";
  if (current.length === 0 || current.length !== windows.length) return "unknown";
  return current.every(
    (window) =>
      Number.isFinite(window.usedPercent) && window.usedPercent >= 0 && window.usedPercent < 100,
  )
    ? "available"
    : "unknown";
}

export type UsageLimitRecoveryState =
  | { readonly kind: "available"; readonly account: ProviderUsageAccountView["account"] }
  | { readonly kind: "loading" | "error" | "unknown" | "unsupported" | "exhausted" };

export function resolveUsageLimitRecovery(input: {
  readonly provider: string;
  readonly effectiveAccountId: string | null;
  readonly accounts: ReadonlyArray<ProviderUsageAccountView>;
  readonly isPending: boolean;
  readonly isError: boolean;
}): UsageLimitRecoveryState {
  if (input.isError) return { kind: "error" };
  if (input.isPending) return { kind: "loading" };
  if (input.provider !== "codex") return { kind: "unsupported" };
  if (input.effectiveAccountId === null) return { kind: "unknown" };
  const alternatives = input.accounts.filter(
    ({ account }) =>
      account.provider === input.provider &&
      account.providerAccountId !== input.effectiveAccountId &&
      !account.needsReconnect,
  );
  const available = alternatives.find(
    ({ snapshot }) => recoveryAccountCapacity(snapshot) === "available",
  );
  if (available) return { kind: "available", account: available.account };
  return {
    kind: alternatives.some(({ snapshot }) => recoveryAccountCapacity(snapshot) === "unknown")
      ? "unknown"
      : "exhausted",
  };
}

export function usageLimitRecoveryDescription(state: UsageLimitRecoveryState): string {
  switch (state.kind) {
    case "available":
      return `Continue this conversation with ${state.account.accountEmail ?? "another connected account"}. Your history stays here.`;
    case "loading":
      return "Checking your accounts…";
    case "error":
      return "Couldn't check account usage. Try again.";
    case "unknown":
      return "Usage is unavailable for one or more accounts. Choose an account to try, or check usage again. Your history stays here.";
    case "unsupported":
      return "Account switching is not available for this provider yet. Wait for usage to reset.";
    case "exhausted":
      return "All connected Codex accounts have reached their usage limits. Connect a new account or wait for usage to reset.";
  }
}

/**
 * The headline must say what actually happened (#3387): a failed usage read is
 * not a usage limit, and an account with headroom is not a limit either. Only
 * the exhausted and unsupported states claim the limit, because only there did
 * a limit genuinely stop the turn.
 */
export function usageLimitRecoveryTitle(state: UsageLimitRecoveryState): string {
  switch (state.kind) {
    case "available":
      return "Switch accounts to continue";
    case "loading":
      return "Checking usage…";
    case "error":
    case "unknown":
      return "Usage could not be checked";
    case "unsupported":
    case "exhausted":
      return "Usage limit reached";
  }
}

/** The collapsed composer trigger speaks for the dialog before it opens. */
export function usageLimitRecoveryTriggerLabel(state: UsageLimitRecoveryState): string {
  const title = usageLimitRecoveryTitle(state);
  return state.kind === "available" || state.kind === "loading"
    ? title
    : `${title}. View recovery options`;
}
